import styled from '@emotion/styled';
import { Box, Button, Grid, Tooltip, Typography, tooltipClasses } from '@mui/material';
import PropTypes from 'prop-types';
import { memo, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useHistory } from 'react-router-dom';
import { BounceLoader } from 'react-spinners';
import { useAnalytics } from 'use-analytics';
import { ReactComponent as UploadIcon } from '../../../src/assets/pulse-image-upload-icon.svg';
import { MAX_PULSE_JOURNEY_ASSETS } from '../../modules/automated-insights/constants';
import { getFilesWithError } from '../../modules/automated-insights/helpers';
import { Paths } from '../../routes';

const MAX_FILE_SIZE = 20000000;
const ACCEPTED_FILE_TYPES = {
  'image/jpeg': [],
  'image/png': [],
};

const FILE_ERROR_CODES = {
  FILE_TOO_LARGE: 'file-too-large',
  TOO_MANY_FILES: 'too-many-files',
};

const DisableFeaturesTooltip = styled(({ className, maxWidth, height, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme, maxWidth, height }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#FCFCFC',
    fontSize: '12px',
    color: theme.palette.text.primary,
    borderRadius: '24px',
    border: '1px solid #E0E0E0',
    height: height || '140px',
    minWidth: '340px',
  },
}));

const ExploreButton = styled(Button)(({ theme }) => ({
  height: '28px',
  width: '105px',
  borderRadius: '27px',
  border: '1px solid #082739',
  color: 'rgba(9, 41, 60, 1)',
}));

const FileTooLargeError = ({ fileRejections }) => {
  const largeFiles = getFilesWithError(fileRejections, FILE_ERROR_CODES.FILE_TOO_LARGE);

  if (largeFiles.length === 0) return <></>;

  return (
    <Box>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
        Files must be less than 20 MB:
      </Typography>
      <Box>
        {largeFiles?.map((file, index) => (
          <Typography
            key={`${index}-${file?.file?.name}`}
            variant="caption"
            sx={{
              display: 'block',
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
            {file?.file?.name}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

const AssetUploadCard = ({
  handleFileInput,
  multiple,
  iconColor,
  isUploading,
  maxFiles = 1,
  background,
  disableFeatures,
  freeTrialExpired,
  subscriptionExpired,
  reachedUsageLimit,
  user,
}) => {
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    accept: ACCEPTED_FILE_TYPES,
    maxFiles: maxFiles,
    maxSize: MAX_FILE_SIZE,
    multiple: multiple,
    onDrop: handleFileInput,
  });

  const history = useHistory();
  const { track } = useAnalytics();

  const redirectToSubscriptions = useCallback(() => {
    track(TrackEvent.CLICKED_BUTTON_TO_EXPLORE_PLANS_FROM_BLOCKED_FEATURE);
    history.push(`${Paths.automatedInsights.basePath}/subscription`);
  }, [history, track]);

  const getTooltipContent = () => {
    if (disableFeatures) {
      if (freeTrialExpired) {
        return (
          <Box sx={{ marginTop: -1 }}>
            <Box mb={2} sx={{ marginLeft: 1, marginTop: 2 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                <b>Your free trial has ended.</b>
              </Typography>
            </Box>
            <Box sx={{ marginLeft: 1, marginTop: -1 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                Ready to keep the momentum going? Subscribe now to unlock full access.
              </Typography>
            </Box>
            <Box sx={{ marginRight: 1, marginTop: 2, textAlign: 'end' }}>
              <ExploreButton onClick={redirectToSubscriptions}>
                <Typography sx={{ textTransform: 'none', fontSize: '12px' }}>
                  <b>Explore plans</b>
                </Typography>
              </ExploreButton>
            </Box>
          </Box>
        );
      } else if (subscriptionExpired) {
        return (
          <Box sx={{ marginTop: -1 }}>
            <Box mb={2} sx={{ marginLeft: 1, marginTop: 2 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                <b>Subscription Expired</b>
              </Typography>
            </Box>
            <Box sx={{ marginLeft: 1, marginTop: -1 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                {user?.isBillingAdmin
                  ? `Your subscription to Pulse has expired. Upgrade to a full subscription today and continue transforming your digital assets with the power of WEVO Pulse.`
                  : `Your subscription to Pulse has expired. Please contact your administrator to upgrade to 
                  a full subscription and continue transforming your digital assets with the power of WEVO Pulse.`}{' '}
              </Typography>
            </Box>
            {user?.isBillingAdmin && (
              <Box sx={{ marginRight: 1, textAlign: 'end' }}>
                <ExploreButton onClick={redirectToSubscriptions}>
                  <Typography sx={{ textTransform: 'none', fontSize: '12px' }}>
                    <b>Explore plans</b>
                  </Typography>
                </ExploreButton>
              </Box>
            )}
          </Box>
        );
      } else if (reachedUsageLimit) {
        return (
          <Box sx={{ marginTop: -1 }}>
            <Box mb={2} sx={{ marginLeft: 1, marginTop: 2 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                <b>Usage Limit Reached!</b>
              </Typography>
            </Box>
            <Box sx={{ marginLeft: 1, marginTop: -1 }}>
              <Typography variant="body1" sx={{ fontSize: '13px' }}>
                You’ve reached your usage limit for the current plan. Upgrade to unlock additional capacity.
              </Typography>
            </Box>
            <Box sx={{ marginRight: 1, marginTop: 2, textAlign: 'end' }}>
              <ExploreButton onClick={redirectToSubscriptions}>
                <Typography sx={{ textTransform: 'none', fontSize: '12px' }}>
                  <b>Explore plans</b>
                </Typography>
              </ExploreButton>
            </Box>
          </Box>
        );
      }
      return '';
    }
    return '';
  };

  const hasFileTooLarge = useMemo(() => {
    const largeFiles = getFilesWithError(fileRejections, FILE_ERROR_CODES.FILE_TOO_LARGE);
    return largeFiles?.length > 0;
  }, [fileRejections]);

  const tooManyFilesError = useMemo(() => {
    if (fileRejections.length > 0) {
      const filesExceedingLimit = getFilesWithError(fileRejections, FILE_ERROR_CODES.TOO_MANY_FILES);
      if (filesExceedingLimit.length === 0) return '';

      return `You can only upload up to ${MAX_PULSE_JOURNEY_ASSETS} files`;
    }
    return '';
  }, [fileRejections]);

  const getCardContent = () => {
    return (
      <>
        {!isUploading && (
          <>
            <input {...getInputProps()} disabled={disableFeatures} />
            <Grid container item direction="column">
              <Grid item xs={12} sx={{ width: '100%' }}>
                <Box>
                  <Box my={2}>
                    <UploadIcon style={{ color: isDragActive && !isDragReject ? '#FFFFFF' : iconColor }} />
                  </Box>
                  <Typography color="primary" variant="body2">
                    Drag and drop or{' '}
                    <Typography component="span" variant="body2" sx={{ color: '#43BCFF' }}>
                      Choose{' '}
                    </Typography>
                    one or multiple files to upload
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(221, 221, 221, 0.83)' }}>
                    PNG, JPG or JPEG
                  </Typography>
                </Box>
              </Grid>
              {isDragReject && (
                <Grid item xs={12} mt={1} sx={{ width: '100%' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Sorry, either the file type is not accepted or too many files were uploaded.
                  </Typography>
                </Grid>
              )}
              {hasFileTooLarge && (
                <Grid item xs={12} mt={1} sx={{ width: '100%' }}>
                  <FileTooLargeError fileRejections={fileRejections} />
                </Grid>
              )}
              {tooManyFilesError && (
                <Grid item xs={12} mt={1} sx={{ width: '100%' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {tooManyFilesError}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </>
        )}
        {isUploading && (
          <Grid container direction="column" alignItems="center" spacing={2}>
            <Grid item>
              <BounceLoader color="#FFFFFF" size={40} aria-label="loading spinner" />
            </Grid>
            <Grid item>
              <Typography sx={{ color: 'text.secondary' }}>Uploading...</Typography>
            </Grid>
          </Grid>
        )}
      </>
    );
  };

  const getDisableFeaturesTooltipHeight = () => {
    if (subscriptionExpired) {
      return user?.isBillingAdmin ? '160px' : '130px';
    }

    return '';
  };

  return (
    <Box
      sx={{
        cursor: disableFeatures ? 'not-allowed' : 'pointer',
        background: (theme) => background || theme.palette.gradient.main,
        borderRadius: '10px',
      }}>
      <DisableFeaturesTooltip title={getTooltipContent()} height={getDisableFeaturesTooltipHeight()}>
        <Grid
          container
          sx={{ minHeight: 175, border: '1px solid rgba(217, 217, 217, 0.2)', borderRadius: '10px' }}>
          {disableFeatures ? (
            <Grid
              container
              item
              alignItems="center"
              sx={{
                textAlign: 'center',
                padding: 2,
              }}>
              {getCardContent()}
            </Grid>
          ) : (
            <Grid
              container
              item
              alignItems="center"
              {...getRootProps()}
              sx={{
                textAlign: 'center',
                padding: 2,
              }}>
              {getCardContent()}
            </Grid>
          )}
        </Grid>
      </DisableFeaturesTooltip>
    </Box>
  );
};

AssetUploadCard.propTypes = {
  handleFileInput: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  background: PropTypes.string,
  iconColor: PropTypes.string.isRequired,
  isUploading: PropTypes.bool.isRequired,
};

AssetUploadCard.defaultProps = {
  multiple: false,
};

export default memo(AssetUploadCard);
